import React, { createContext, useContext, useMemo, useState } from "react";

export type TimezoneOption = {
  value: string;
  label: string;
};

export const STORAGE_KEY_TIMEZONE = "tb_admin_timezone";

export function detectClientTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

export const COMMON_TIMEZONES: readonly TimezoneOption[] = [
  { value: "CLIENT", label: "Local browser" },
  { value: "UTC", label: "UTC (Coordinated Universal Time)" },
  { value: "Asia/Bangkok", label: "Bangkok (ICT, UTC+07:00)" },
  { value: "Asia/Singapore", label: "Singapore (SGT, UTC+08:00)" },
  { value: "Asia/Tokyo", label: "Tokyo (JST, UTC+09:00)" },
  { value: "Europe/London", label: "London (GMT/BST)" },
  { value: "Europe/Berlin", label: "Berlin (CET/CEST)" },
  { value: "America/New_York", label: "New York (ET)" },
  { value: "America/Los_Angeles", label: "Los Angeles (PT)" },
] as const;

export function resolveTimezone(tzSetting: string): string {
  if (!tzSetting || tzSetting === "CLIENT") {
    return detectClientTimezone();
  }
  return tzSetting;
}

export function getTimezoneAbbreviation(timezone: string, date = new Date()): string {
  const resolved = resolveTimezone(timezone);
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: resolved,
      timeZoneName: "short",
    }).formatToParts(date);
    const tzPart = parts.find((p) => p.type === "timeZoneName");
    return tzPart ? tzPart.value : resolved;
  } catch {
    return resolved;
  }
}

export function formatDateTimeWithTz(
  value: string | null | undefined,
  timezone: string,
  fallback = "—",
): string {
  if (!value) return fallback;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;
  const resolved = resolveTimezone(timezone);
  try {
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: resolved,
      timeZoneName: "short",
    }).format(date);
  } catch {
    return date.toISOString();
  }
}

export function formatDateWithTz(
  value: string | null | undefined,
  timezone: string,
  fallback = "Perpetual",
): string {
  if (!value) return fallback;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;
  const resolved = resolveTimezone(timezone);
  try {
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      timeZone: resolved,
    }).format(date);
  } catch {
    return date.toISOString().slice(0, 10);
  }
}

type TimezoneContextValue = {
  timezoneSetting: string;
  resolvedTimezone: string;
  timezoneAbbr: string;
  setTimezoneSetting: (tz: string) => void;
  formatDateTime: (value: string | null | undefined, fallback?: string) => string;
  formatDate: (value: string | null | undefined, fallback?: string) => string;
};

const TimezoneContext = createContext<TimezoneContextValue | null>(null);

export function TimezoneProvider({ children }: { children: React.ReactNode }) {
  const [timezoneSetting, setTimezoneSettingState] = useState<string>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY_TIMEZONE);
      if (saved) return saved;
    } catch {
      // Fall through to default
    }
    return "CLIENT";
  });

  const resolvedTimezone = useMemo(() => resolveTimezone(timezoneSetting), [timezoneSetting]);
  const timezoneAbbr = useMemo(() => getTimezoneAbbreviation(resolvedTimezone), [resolvedTimezone]);

  const setTimezoneSetting = (tz: string) => {
    setTimezoneSettingState(tz);
    try {
      localStorage.setItem(STORAGE_KEY_TIMEZONE, tz);
    } catch {
      // Ignore storage error
    }
  };

  const value = useMemo<TimezoneContextValue>(() => ({
    timezoneSetting,
    resolvedTimezone,
    timezoneAbbr,
    setTimezoneSetting,
    formatDateTime: (val, fallback) => formatDateTimeWithTz(val, resolvedTimezone, fallback),
    formatDate: (val, fallback) => formatDateWithTz(val, resolvedTimezone, fallback),
  }), [timezoneSetting, resolvedTimezone, timezoneAbbr]);

  return (
    <TimezoneContext.Provider value={value}>
      {children}
    </TimezoneContext.Provider>
  );
}

export function useTimezone(): TimezoneContextValue {
  const context = useContext(TimezoneContext);
  if (!context) {
    const clientTz = detectClientTimezone();
    return {
      timezoneSetting: "CLIENT",
      resolvedTimezone: clientTz,
      timezoneAbbr: getTimezoneAbbreviation(clientTz),
      setTimezoneSetting: () => {},
      formatDateTime: (val, fallback) => formatDateTimeWithTz(val, clientTz, fallback),
      formatDate: (val, fallback) => formatDateWithTz(val, clientTz, fallback),
    };
  }
  return context;
}
